import type { StateCreator } from 'zustand';
import { api } from '../services/api';
import type { StoreState, Item, Spell, ImageCategory, LoreCategory, SoundscapeCategory, Monster, Deity, TerrainType, Soundscape, Treasure, WeatherCondition } from '../types/types';
import { logger } from '../services/logger.client';

export interface CacheState {
    items: Item[];
    spells: Spell[];
    imageCategories: ImageCategory[];
    loreCategories: LoreCategory[];
    soundscapeCategories: SoundscapeCategory[];
    monsterRoster: Monster[];
    deities: Deity[];
    terrainTypes: TerrainType[];
    soundscapes: Soundscape[];
    treasures: Treasure[];
    weatherConditions: WeatherCondition[];
}

export interface CacheActions {
  addMonster: (monster: Omit<Monster, 'id'>) => void;
  updateMonster: (monster: Partial<Monster> & { id: number }) => void;
  removeMonster: (id: number) => void;
  cloneMonster: (id: number) => void;
  addDeity: (deity: Omit<Deity, 'id'>) => void;
  updateDeity: (deity: Partial<Deity> & { id: number }) => void;
  deleteDeity: (id: number) => void;
  addLoreCategory: (category: Omit<LoreCategory, 'id'>) => Promise<void>;
  updateLoreCategory: (category: Partial<LoreCategory> & { id: number }) => Promise<void>;
  deleteLoreCategory: (id: number) => Promise<void>;
  updateLoreCategoriesSort: (newCategories: LoreCategory[]) => Promise<void>;
  addImageCategory: (category: Omit<ImageCategory, 'id'>) => Promise<void>;
  updateImageCategory: (category: Partial<ImageCategory> & { id: number }) => Promise<void>;
  updateImageCategoriesSort: (newCategories: ImageCategory[]) => Promise<void>;
  deleteImageCategory: (id: number) => Promise<void>;
  addTerrainType: (terrainType: Omit<TerrainType, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateTerrainType: (terrainType: Partial<TerrainType> & { id: number }) => Promise<void>;
  deleteTerrainType: (id: number) => Promise<void>;
  addSoundscape: (soundscape: Omit<Soundscape, 'id' | 'createdAt'>) => Promise<void>;
  updateSoundscape: (soundscape: Partial<Soundscape> & { id: number }) => Promise<void>;
  deleteSoundscape: (id: number) => Promise<void>;
  addSoundscapeCategory: (category: Omit<SoundscapeCategory, 'id'>) => Promise<void>;
  updateSoundscapeCategory: (category: Partial<SoundscapeCategory> & { id: number }) => Promise<void>;
  deleteSoundscapeCategory: (id: number) => Promise<void>;
  addItem: (item: Omit<Item, 'id'>) => Promise<void>;
  updateItem: (item: Partial<Item> & { id: number }) => Promise<void>;
  deleteItem: (id: number) => Promise<void>;
  fetchItems: () => Promise<void>;
  addSpell: (spell: Omit<Spell, 'id'>) => Promise<void>;
  updateSpell: (spell: Partial<Spell> & { id: number; }) => Promise<void>;
  deleteSpell: (id: number) => Promise<void>;
  addTreasure: (treasure: Omit<Treasure, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateTreasure: (treasure: Partial<Treasure> & { id: number }) => Promise<void>;
  deleteTreasure: (id: number) => Promise<void>;
  fetchTreasures: () => Promise<void>;
  addWeatherCondition: (condition: Omit<WeatherCondition, 'id'>) => Promise<void>;
  updateWeatherCondition: (condition: Partial<WeatherCondition> & { id: number }) => Promise<void>;
  deleteWeatherCondition: (id: number) => Promise<void>;
}

export const initialCacheState: CacheState = {
  monsterRoster: [],
  deities: [],
  loreCategories: [],
  imageCategories: [],
  terrainTypes: [],
  soundscapeCategories: [],
  items: [],
  spells: [],
  soundscapes: [],
  treasures: [],
  weatherConditions: [],
};

export const createCacheSlice: StateCreator<
  StoreState,
  [['zustand/immer', never]],
  [],
  { cache: CacheState & CacheActions }
> = (set, get) => ({
  cache: {
    ...initialCacheState,
    addMonster: (monster) => {
      logger.info({ monster }, 'Adding monster to roster');
      // In a real app, this would be an async call to the backend.
      // For now, we'll just add it to the local state with a temporary ID.
      const newMonster = { ...monster, id: Date.now() } as Monster;
      set(state => { state.session.cache.monsterRoster.push(newMonster); });
    },
    updateMonster: (monster) => {
      logger.info({ monster }, 'Updating monster in roster');
      set(state => {
        const index = (state.session.cache.monsterRoster ?? []).findIndex(m => m.id === monster.id);
        if (index !== -1) {
          state.session.cache.monsterRoster[index] = { ...state.session.cache.monsterRoster[index]!, ...monster };
        }
      });
    },
    removeMonster: (id) => {
      logger.info({ id }, 'Removing monster from roster');
      set(state => { state.session.cache.monsterRoster = (state.session.cache.monsterRoster ?? []).filter(m => m.id !== id); });
    },
    cloneMonster: (id) => {
      logger.info({ id }, 'Cloning monster in roster');
      const monsterToClone = (get().session.cache.monsterRoster ?? []).find(m => m.id === id);
      if (monsterToClone) {
        const newMonster = { ...monsterToClone, id: Date.now(), name: `${monsterToClone.name} (Copy)` };
        set(state => { state.session.cache.monsterRoster.push(newMonster); });
      } else {
        logger.warn({ id }, 'Could not find monster to clone');
      }
    },
    addDeity: (deity) => {
      const newDeity = { ...deity, id: Date.now() } as Deity;
      set(state => { state.session.cache.deities.push(newDeity); });
    },
    updateDeity: (deity) => {
      set(state => {
        const index = (state.session.cache.deities ?? []).findIndex(d => d.id === deity.id);
        if (index !== -1) {
          state.session.cache.deities[index] = { ...state.session.cache.deities[index]!, ...deity };
        }
      });
    },
    deleteDeity: (id) => {
      set(state => { state.session.cache.deities = (state.session.cache.deities ?? []).filter(d => d.id !== id); });
    },
    addLoreCategory: async (category) => {
      logger.info({ category }, 'Adding lore category');
      const response = await api('/api/lore-categories', {
        method: 'POST',
        body: JSON.stringify(category),
      });
      const newCategory = await response.json() as LoreCategory;
      set(state => { state.session.cache.loreCategories.push(newCategory); });
    },
    updateLoreCategory: async (category) => {
      logger.info({ category }, 'Updating lore category');
      const response = await api(`/api/lore-categories/${category.id}`, { method: 'PUT', body: JSON.stringify(category) });
      const updatedCategory = await response.json() as LoreCategory;
      set(state => {
        const index = (state.session.cache.loreCategories ?? []).findIndex(c => c.id === updatedCategory.id);
        if (index !== -1) state.session.cache.loreCategories[index] = updatedCategory;
      });
    },
    deleteLoreCategory: async (id) => {
      await api(`/api/lore-categories/${id}`, { method: 'DELETE' });
      set(state => { state.session.cache.loreCategories = (state.session.cache.loreCategories ?? []).filter(c => c.id !== id); });
    },
    updateLoreCategoriesSort: async (newCategories) => {
      const originalCategories = get().session.cache.loreCategories;
      // Optimistically update the UI
      set(state => { state.session.cache.loreCategories = newCategories; });

      try {
        const orderedIds = (newCategories ?? []).map(c => c.id);
        await api('/api/lore-categories/sort', { method: 'PUT', body: JSON.stringify({ orderedIds }) });
      } catch (err) {
        logger.error(err, 'Failed to save lore category order, rolling back.');
        set(state => { state.session.cache.loreCategories = originalCategories; });
        throw err;
      }
    },
    addImageCategory: async (category) => {
      logger.info({ category }, 'Adding image category');
      const response = await api('/api/image-categories', {
        method: 'POST',
        body: JSON.stringify(category),
      });
      const newCategory = await response.json() as ImageCategory;
      set(state => { state.session.cache.imageCategories.push(newCategory); });
    },
    updateImageCategory: async (category) => {
      logger.info({ category }, 'Updating image category');
      const response = await api(`/api/image-categories/${category.id}`, { method: 'PUT', body: JSON.stringify(category) });
      const updatedCategory = await response.json() as ImageCategory;
      set(state => {
        const index = (state.session.cache.imageCategories ?? []).findIndex(c => c.id === updatedCategory.id);
        if (index !== -1) state.session.cache.imageCategories[index] = updatedCategory;
      });
    },
    deleteImageCategory: async (id) => {
      await api(`/api/image-categories/${id}`, { method: 'DELETE' });
      set(state => { state.session.cache.imageCategories = (state.session.cache.imageCategories ?? []).filter(c => c.id !== id); });
    },
    updateImageCategoriesSort: async (newCategories) => {
      const originalCategories = get().session.cache.imageCategories;
      // Optimistically update the UI
      set(state => { state.session.cache.imageCategories = newCategories; });

      try {
        const orderedIds = (newCategories ?? []).map(c => c.id);
        await api('/api/image-categories/sort', { method: 'PUT', body: JSON.stringify({ orderedIds }) });
      } catch (err) {
        logger.error(err, 'Failed to save image category order, rolling back.');
        set(state => { state.session.cache.imageCategories = originalCategories; });
        throw err;
      }
    },
    addTerrainType: async (terrainType) => {
      logger.info({ terrainType }, 'Adding terrain type');
      const response = await api('/api/terrain-types', { method: 'POST', body: JSON.stringify(terrainType) });
      const newTerrainType = await response.json() as TerrainType;
      set(state => { state.session.cache.terrainTypes.push(newTerrainType); });
    },
    updateTerrainType: async (terrainType) => {
      logger.info({ terrainType }, 'Updating terrain type');
      const response = await api(`/api/terrain-types/${terrainType.id}`, { method: 'PUT', body: JSON.stringify(terrainType) });
      const updatedTerrainType = await response.json() as TerrainType;
      set(state => {
        const index = (state.session.cache.terrainTypes ?? []).findIndex(t => t.id === updatedTerrainType.id);
        if (index !== -1) state.session.cache.terrainTypes[index] = updatedTerrainType;
      });
    },
    deleteTerrainType: async (id) => {
      logger.info({ id }, 'Deleting terrain type');
      await api(`/api/terrain-types/${id}`, { method: 'DELETE' });
      set(state => { state.session.cache.terrainTypes = (state.session.cache.terrainTypes ?? []).filter(t => t.id !== id); });
    },
    addSoundscape: async (soundscape) => {
      logger.info({ soundscape }, 'Adding soundscape');
      const response = await api('/api/soundscapes', { method: 'POST', body: JSON.stringify(soundscape) });
      const newSoundscape = await response.json() as Soundscape;
      set(state => { state.session.cache.soundscapes.push(newSoundscape); });
    },
    updateSoundscape: async (soundscape) => {
      logger.info({ soundscape }, 'Updating soundscape');
      const response = await api(`/api/soundscapes/${soundscape.id}`, { method: 'PUT', body: JSON.stringify(soundscape) });
      const updatedSoundscape = await response.json() as Soundscape;
      set(state => {
        const index = (state.session.cache.soundscapes ?? []).findIndex(s => s.id === updatedSoundscape.id);
        if (index !== -1) state.session.cache.soundscapes[index] = updatedSoundscape;
      });
    },
    deleteSoundscape: async (id) => {
      await api(`/api/soundscapes/${id}`, { method: 'DELETE' });
      set(state => { state.session.cache.soundscapes = (state.session.cache.soundscapes ?? []).filter(s => s.id !== id); });
    },
    addSoundscapeCategory: async (category) => {
      const response = await api('/api/soundscape-categories', {
        method: 'POST',
        body: JSON.stringify(category),
      });
      const newCategory = await response.json() as SoundscapeCategory;
      set(state => { state.session.cache.soundscapeCategories.push(newCategory); });
    },
    updateSoundscapeCategory: async (category) => {
      const response = await api(`/api/soundscape-categories/${category.id}`, { method: 'PUT', body: JSON.stringify(category) });
      const updatedCategory = await response.json() as SoundscapeCategory;
      set(state => {
        const index = (state.session.cache.soundscapeCategories ?? []).findIndex(c => c.id === updatedCategory.id);
        if (index !== -1) state.session.cache.soundscapeCategories[index] = updatedCategory;
      });
    },
    deleteSoundscapeCategory: async (id) => {
      await api(`/api/soundscape-categories/${id}`, { method: 'DELETE' });
      set(state => { state.session.cache.soundscapeCategories = (state.session.cache.soundscapeCategories ?? []).filter(c => c.id !== id); });
    },
    addItem: async (item) => {
        const tempId = -Date.now();
        const tempItem = { ...item, id: tempId };
        set(state => { state.session.cache.items.push(tempItem as Item); });
        const response = await api('/api/items', { method: 'POST', body: JSON.stringify(item) });
        const newItem = await response.json() as Item;
        set(state => {
            const index = (state.session.cache.items ?? []).findIndex(i => i.id === tempId);
            if (index !== -1) state.session.cache.items[index] = newItem;
        });
    },
    updateItem: async (item) => {
      const response = await api(`/api/items/${item.id}`, {
        method: 'PUT',
        body: JSON.stringify(item),
      });
      const updatedItem = await response.json() as Item;
      set(state => {
        const index = (state.session.cache.items ?? []).findIndex((i: Item) => i.id === updatedItem.id);
        if (index !== -1) state.session.cache.items[index] = updatedItem;
      });
    },
    deleteItem: async (id) => {
      await api(`/api/items/${id}`, { method: 'DELETE' });
      set(state => { state.session.cache.items = (state.session.cache.items ?? []).filter((i: Item) => i.id !== id); });
    },
    fetchItems: async () => {
      const response = await api('/api/items');
      const items = await response.json() as Item[];
      set(state => { state.session.cache.items = items; });
    },
    addSpell: async (spell) => {
        const tempId = -Date.now();
        const tempSpell = { ...spell, id: tempId };
        set(state => { state.session.cache.spells.push(tempSpell as Spell); });

        try {
            const response = await api('/api/spells', { method: 'POST', body: JSON.stringify(spell) });
            const newSpell = await response.json() as Spell;
            set(state => {
                const index = (state.session.cache.spells ?? []).findIndex(s => s.id === tempId);
                if (index !== -1) state.session.cache.spells[index] = newSpell;
            });
        } catch (error) {
            logger.error(error, 'Error adding spell, rolling back.');
            set(state => { state.session.cache.spells = (state.session.cache.spells ?? []).filter(s => s.id !== tempId); });
            throw error;
        }
    },
    updateSpell: async (spell) => {
        const originalSpells = get().session.cache.spells;
        set(state => {
            const spellToUpdate = (state.session.cache.spells ?? []).find(s => s.id === spell.id);
            if (spellToUpdate) {
                Object.assign(spellToUpdate, spell);
            }
        });

        try {
            await api(`/api/spells/${spell.id}`, { method: 'PUT', body: JSON.stringify(spell) });
        } catch (error) {
            logger.error(error, `Error updating spell ${spell.id}, rolling back.`);
            set(state => { state.session.cache.spells = originalSpells; });
            throw error;
        }
    },
    deleteSpell: async (id) => {
        const originalSpells = get().session.cache.spells;
        set(state => { state.session.cache.spells = (state.session.cache.spells ?? []).filter(s => s.id !== id); });

        try {
            await api(`/api/spells/${id}`, { method: 'DELETE' });
        } catch (error) {
            logger.error(error, `Error deleting spell ${id}, rolling back.`);
            set(state => { state.session.cache.spells = originalSpells; });
            throw error;
        }
    },
    addTreasure: async (treasure) => {
      const tempId = -Date.now();
      const tempTreasure = { ...treasure, id: tempId };
      set(state => { state.session.cache.treasures.push(tempTreasure as Treasure); });

      try {
        const response = await api('/api/treasures', { method: 'POST', body: JSON.stringify(treasure) });
        const newTreasure = await response.json() as Treasure;
        set(state => {
          const index = (state.session.cache.treasures ?? []).findIndex((t: Treasure) => t.id === tempId);
          if (index !== -1) state.session.cache.treasures[index] = newTreasure;
        });
      } catch (error) {
        logger.error(error, 'Error adding treasure, rolling back.');
        set(state => { state.session.cache.treasures = (state.session.cache.treasures ?? []).filter((t: Treasure) => t.id !== tempId); });
        throw error;
      }
    },
    updateTreasure: async (treasure) => {
      const originalTreasures = get().session.cache.treasures;
      const treasureIndex = (originalTreasures ?? []).findIndex(t => t.id === treasure.id);
      if (treasureIndex === -1) return;

      set(state => { state.session.cache.treasures[treasureIndex] = { ...state.session.cache.treasures[treasureIndex]!, ...treasure }; });

      try {
        await api(`/api/treasures/${treasure.id}`, { method: 'PUT', body: JSON.stringify(treasure) });
      } catch (error) {
        logger.error(error, `Error updating treasure ${treasure.id}, rolling back.`);
        set(state => { state.session.cache.treasures = originalTreasures; });
        throw error;
      }
    },
    deleteTreasure: async (id) => {
      const originalTreasures = get().session.cache.treasures;
      set(state => { state.session.cache.treasures = (originalTreasures ?? []).filter(t => t.id !== id); });

      try {
        await api(`/api/treasures/${id}`, { method: 'DELETE' });
      } catch (error) {
        logger.error(error, `Error deleting treasure ${id}, rolling back.`);
        set(state => { state.session.cache.treasures = originalTreasures; });
        throw error;
      }
    },
    fetchTreasures: async () => {
      const response = await api('/api/treasures');
      const treasures = await response.json() as Treasure[];
      set(state => { state.session.cache.treasures = treasures; });
    },
    addWeatherCondition: async (condition) => {
      const response = await api('/api/weather-conditions', { method: 'POST', body: JSON.stringify(condition) });
      const newCondition = await response.json() as WeatherCondition;
      set(state => { state.session.cache.weatherConditions.push(newCondition); });
    },
    updateWeatherCondition: async (condition) => {
      const originalConditions = get().session.cache.weatherConditions;
      set(state => {
        const index = (state.session.cache.weatherConditions ?? []).findIndex((c: WeatherCondition) => c.id === condition.id);
        if (index !== -1) state.session.cache.weatherConditions[index] = { ...state.session.cache.weatherConditions[index]!, ...condition };
      });
      try {
        await api(`/api/weather-conditions/${condition.id}`, { method: 'PUT', body: JSON.stringify(condition) });
      } catch (error) {
        logger.error(error, `Error updating weather condition ${condition.id}, rolling back.`);
        set(state => { state.session.cache.weatherConditions = originalConditions; });
        throw error;
      }
    },
    deleteWeatherCondition: async (id) => {
      const originalConditions = get().session.cache.weatherConditions;
      set(state => { state.session.cache.weatherConditions = (originalConditions ?? []).filter(c => c.id !== id); });
      try {
        await api(`/api/weather-conditions/${id}`, { method: 'DELETE' });
      } catch (error) {
        logger.error(error, `Error deleting weather condition ${id}, rolling back.`);
        set(state => { state.session.cache.weatherConditions = originalConditions; });
        throw error;
      }
    },
  }
});